/**
 * learnerShowMe — where a learner's Show me link lands (job #419·C, for the
 * support loop, Tom's ruling r-2026-10-21-ssi-support-answers-lead-with-a).
 *
 * A support reply to a learner leads with https://saysomethingin.app/show-me/<walk-id>.
 * The /show-me/:walk route asks this where to go, and does what the Handbook
 * and the admin Show me page already do: defer the walk, then take the reader
 * to the page it runs on, where that page's own surface (PlayerContainer,
 * WalkOffer in Settings, HowThisWorksLibrary) claims it and plays it. No new
 * walk engine. A walk a learner does not have, or no walk at all, goes to the
 * public Handbook page of that name; the public-handbook route already sends
 * a page the server does not have back home.
 */
import type { Walk, WalkPersona } from './useWalkthrough'
import { walkVisibleTo } from './persona'

/** The learner's places and the player address each opens on (as launchPlan). */
const LEARNER_PLACES: Record<string, string> = {
  player: '/',
  'player-settings': '/?screen=settings',
  library: '/?screen=library',
}

export interface LearnerShowMe {
  /** The walk to defer before going, or null when nothing is played. */
  defer: string | null
  /** Where to go. */
  to: string
  /** True when `to` is the static public Handbook, which lives outside the SPA. */
  external: boolean
}

export function learnerShowMe(id: string, walk: Walk | null, persona?: WalkPersona | null): LearnerShowMe {
  // A walk not for this viewer's persona is not played, listed or linked: quiet
  // redirect to the in-app Handbook home (job #186). No persona = signed out.
  if (walk && persona && !walkVisibleTo(walk, persona)) return { defer: null, to: '/app-handbook/', external: false }
  const to = walk?.personas.includes('learner') ? LEARNER_PLACES[walk.place.route] : undefined
  if (walk && to) return { defer: walk.id, to, external: false }
  // Only a plain kebab-case name is ever a Handbook page (handbook-web's own rule).
  if (/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id)) return { defer: null, to: `/handbook/${id}/`, external: true }
  return { defer: null, to: '/', external: false }
}

/**
 * Where a public Handbook address the server has no page for lands (job #434).
 * A signed-out visitor's /show-me/<staff walk> goes to /handbook/<walk-id>/, and
 * only walks with a recorded film have such a page. A miss used to go home, which
 * for a visitor is the course chooser, a dead end for a prospect looking at a
 * school feature; it now goes to the Handbook index, which is plain HTML and
 * always there. The index itself missing goes home, so this can never loop.
 */
export function handbookMissTarget(fullPath: string): { to: string; external: boolean } {
  const path = fullPath.split(/[?#]/)[0].replace(/\/+$/, '')
  return path === '/handbook' ? { to: '/', external: false } : { to: '/handbook/', external: true }
}
